import { collectPredecessorsOrSuccessors } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import type { Elements } from "@/components/RuleGraph_FullRelease/types";
import type { MsgFromWorker, MsgToWorker } from "@/components/RuleGraph_FullRelease/worker/types";

import { graphlib } from "@dagrejs/dagre";
import dagre from "@dagrejs/dagre";
import styled from "@emotion/styled";
import { ensureParsedGraph, getOrderedGoalNodes } from "@imminently/decisively-core/utils";
import { isMac } from "@imminently/decisively-ui/Kbd";
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  type Edge as ReactFlowEdge,
  type Node as ReactFlowNode,
  type ReactFlowProps,
  ReactFlowProvider,
  isNode,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStore,
} from "@xyflow/react";
import find from "lodash/find";
import React, { useEffect, useId, useMemo, useState } from "react";
import { useUpdateEffect } from "react-use";
import Icon from "@imminently/decisively-ui/Icon";
// import { flushSync } from "react-dom"; // TODO ds just check if we need this anymore

export interface RestrictedView {
  rootPath: string;
  /** where null is no restriction/max */
  graphDepth: number | null;
  visibleNodes: string[] | null;
  nodesToMerge: string[] | null;
}

const ANIMATION_DURATION = 500;

const GraphRefit = (props: {
  refitKey?: string;
  onFit?: () => void;
  elementsState: ElementState;
  centerElementId?: string;
}) => {
  const { refitKey, onFit, elementsState, centerElementId } = props;
  const { setViewport, fitView, setCenter } = useReactFlow();

  const fitViewRef = React.useRef(fitView);
  fitViewRef.current = fitView;

  const refitKeyChanged = React.useRef<boolean>(false);
  const prevRefitKey = React.useRef<string | undefined>(undefined);
  if (prevRefitKey.current !== refitKey) {
    refitKeyChanged.current = true;
    prevRefitKey.current = refitKey;
  }

  const [forceRerender, setForceRerender] = useState<number>(0);

  useEffect(() => {
    if (!refitKeyChanged.current || elementsState.mode === "initializing" || elementsState.mode === "empty") {
      return;
    }
    refitKeyChanged.current = false;
    setTimeout(() => {
      if (centerElementId) {
        const element: any = find(elementsState.elements, ["id", centerElementId]);
        if (element) {
          const x = element.position.x + element.data.width / 2;
          const y = element.position.y + element.data.height / 2;
          // setCenter(x, y, 1, ANIMATION_DURATION);
          setCenter(x, y, { zoom: 1, duration: ANIMATION_DURATION });
          onFit?.();
          return;
        }
      }

      fitViewRef.current(undefined);
      onFit?.();
    }, 20);
  }, [refitKey, elementsState.mode]);

  return null;
};

const StyledControls = styled(Controls)`
  bottom: 10px;
  top: inherit;
`;

const SelectedSearchNodeClsnm = "selected-search-node";

const RelayoutBtnContainer = styled.div`
  position: absolute;
  bottom: 123px;
  left: 14px;
  z-index: 100;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border: 1px solid #eee;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  padding-top: 4px;
  padding-left: 6px;
  background-color: white;

  svg {
    padding-top: 2px;
    padding-left: 2px;
    width: 20px;
    height: 20px;
  }
`;

const worker = (typeof Worker === "undefined" ? undefined : new Worker("/dagreWorker.js")) as Worker;

type ElementState =
  // TODO remove the union
  { mode: "initializing" | "empty"; elements: undefined } | { mode: "initialized" | "refresh"; elements: Elements };

export interface GraphViewNodeProps<N = any> {
  id: string;
  data: {
    height: number;
    width: number;
    node: N;
  };
  includeContextMenu?: boolean;
  xPos?: number;
  yPos?: number;
  isDragging?: boolean;
  isDecisionFlow?: boolean;
}

export interface GraphViewProps<N = any> {
  graph: any;
  children?: React.ReactNode;
  refitKey?: string | number;
  recalculateKey?: string | number;
  nodeRenderLimit?: number;
  nodeComponent: React.ComponentType<GraphViewNodeProps<N>>;
  selectedNode?: string;
  showHidden?: boolean;
  onVisibleNodesChange?: (visibleNodes: string[] | null) => void;
  onRepositionNode?: (nodeId: string, x: number, y: number) => void;
  onNodeClick?: (nodeId: string, node: N) => void;
  onNodeCtrlClick?: (nodeId: string) => void;
  restrictedView?: RestrictedView;
  onRestrictedViewNodesMerge?: (nodes: string[]) => void;
  nodeHeight?: number;
  loading?: boolean;
  /** layout direction */
  rankDir?: "TB" | "BT" | "LR" | "RL";
  flowGraphProps?: ReactFlowProps;
  goal?: string;
}

const GraphView = <N,>(props: GraphViewProps<N>) => {
  const {
    graph,
    goal,
    rankDir,
    loading,
    nodeHeight,
    children,
    restrictedView,
    onNodeClick,
    onNodeCtrlClick,
    onRepositionNode,
    showHidden,
    onVisibleNodesChange,
    selectedNode,
    refitKey,
    recalculateKey,
    nodeRenderLimit,
    nodeComponent,
    flowGraphProps,
  } = props;

  const id = useId();

  const resolvedNodeRenderLimit = nodeRenderLimit ?? 200;
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);

  const [elementsState, setElementsState] = React.useState<ElementState>({
    mode: "empty",
    elements: undefined, // TODO might refactor this out into edge and node separately...not sure yet
  });
  const requiresRefit = React.useRef<boolean>(false);
  const requiresSelectedNodeReview = React.useRef<boolean>(false);

  const ignoreNewPositions = React.useRef<{
    repositionedNodes: { [key: string]: { x: number; y: number } };
    oldPositions: { [key: string]: { x: number; y: number } };
    // works nicely without these, but they might be useful in the future if you want even more control...
    // newNodesRootPos: { x: number, y: number };
    // newNodesRelativePosition: "above" | "below";
  } | null>(null);
  const resetLayout = React.useRef<boolean>(false);
  const additionalVisibleNodes = React.useRef<string[]>([]);

  // Check if this is a flow graph (already a graphlib.Graph with flow structure)
  const isFlowGraph = useMemo(() => {
    if (!graph || typeof graph.node !== "function") {
      return false;
    }
    // Check if any node has flow-specific properties
    const nodeIds = graph.nodes();
    if (nodeIds.length === 0) {
      return false;
    }
    const firstNode = graph.node(nodeIds[0]);
    return firstNode && (firstNode.type === "trigger" || firstNode.type === "attribute");
  }, [graph]);

  const rawGraph = useMemo(() => {
    if (!graph) {
      return graph;
    }

    return typeof graph.node === "function" ? graphlib.json.write(graph) : graph;
  }, [graph]);

  // Use refs to track previous values and prevent infinite loops
  const prevRawGraphRef = React.useRef<string | null>(null);
  const prevRestrictedViewRootPathRef = React.useRef<string | undefined>(undefined);

  // Create a stable key for rawGraph to detect actual changes
  const rawGraphKey = React.useMemo(() => {
    if (!rawGraph) {
      return null;
    }
    try {
      return JSON.stringify(rawGraph);
    } catch {
      return null;
    }
  }, [rawGraph]);

  const parsedGraph = useMemo(() => {
    if (!graph) {
      return graph;
    }

    return ensureParsedGraph(graph);
  }, [graph]);
  const goalNodesOrdered = useMemo(() => {
    return getOrderedGoalNodes(parsedGraph as any, goal, showHidden);
  }, [parsedGraph, goal, showHidden]);

  const repositionNode = (nodeId: string, x: number, y: number) => {
    if (ignoreNewPositions.current) {
      ignoreNewPositions.current.repositionedNodes = {
        ...(ignoreNewPositions.current.repositionedNodes || {}),
        [nodeId]: { x, y },
      };
    } else {
      ignoreNewPositions.current = {
        oldPositions: {},
        repositionedNodes: {
          [nodeId]: { x, y },
        },
      };
    }
  };

  // -- lifecycle

  React.useEffect(() => {
    if (elementsState.elements && elementsState.elements.length) {
      setNodes(elementsState.elements.filter((e) => e.type === "node") as unknown as any[]);
      setEdges(elementsState.elements.filter((e) => e.type !== "node") as unknown as any[]);
    }
  }, [elementsState]);

  const nodeTypes = React.useMemo(
    () => ({
      node: nodeComponent as any,
    }),
    [nodeComponent],
  );

  // -- web worker

  const workerListener = React.useCallback((e: MessageEvent<MsgFromWorker>) => {
    const { id: messageId, elements, visibleNodes } = e.data;

    // console.log("Worker message", e.data);

    if (messageId !== id) {
      return;
    }

    if (ignoreNewPositions.current) {
      console.log("Ignoring new positions");

      for (const newEl of elements) {
        if ((newEl as any).position) {
          const repositionData = ignoreNewPositions.current.repositionedNodes[newEl.id];
          const oldPosData = ignoreNewPositions.current.oldPositions[newEl.id];
          const replacementPosition = repositionData || oldPosData;
          if (replacementPosition) {
            newEl["position"] = { x: replacementPosition.x, y: replacementPosition.y };
          }
        }
      }
      // you don't need to hang onto ignoreNewPositions.current.repositionedNodes, because elements will get saved into
      // `elementsState` next, so this will find its way into ignoreNewPositions.current.oldPositions on the next expansion
      ignoreNewPositions.current = null;
      requiresRefit.current = false;
    }

    // const nodes = elements.filter(e => e.type === "node") as ReactFlowNode[];
    // const edges = elements.filter(e => e.type !== "node") as ReactFlowEdge[];

    for (const el of elements) {
      if (el.type === "node") {
        (el as ReactFlowNode).draggable = true;
      }
    }

    if (resetLayout.current) {
      setElementsState({ mode: "initialized", elements: [] });
      setTimeout(() => {
        setElementsState({ mode: "initialized", elements: elements });
      }, 1);
      resetLayout.current = false;
      ignoreNewPositions.current = null;
    } else {
      setElementsState({ mode: "initialized", elements: elements });
      onVisibleNodesChange?.(visibleNodes ?? null);
    }
    // console.log(`Number of (scoped) visible nodes on-screen is: ${visibleNodes?.length}`);

    if (requiresRefit.current) {
      setInternalRefitKey((prev) => prev + 1);
      requiresRefit.current = false;
    } else if (requiresSelectedNodeReview.current) {
      setInternalRefitKey((prev) => prev + 1);
      requiresSelectedNodeReview.current = false;
    }
  }, []);

  React.useEffect(() => {
    worker.addEventListener("message", workerListener);

    return () => {
      worker.removeEventListener("message", workerListener);
    };
  }, [workerListener]);

  // Process flow graphs directly without worker
  React.useEffect(() => {
    if (isFlowGraph && graph && typeof graph.node === "function") {
      const flowGraph = graph as graphlib.Graph;

      // Create a copy for layout
      const layoutGraph = new graphlib.Graph();
      const graphOptions = flowGraph.graph();
      layoutGraph.setGraph({
        rankdir: rankDir || "LR",
        nodesep: graphOptions.nodesep ?? 80,
        ranksep: graphOptions.ranksep ?? 120,
        ranker: graphOptions.ranker || "tight-tree",
      });

      // Copy nodes - preserve dimensions so dagre can space nodes correctly (min width/height for layout)
      flowGraph.nodes().forEach((nodeId) => {
        const nodeData = flowGraph.node(nodeId) as any;
        if (nodeData) {
          const nodeDataCopy = {
            ...nodeData,
            triggers: nodeData.triggers ? nodeData.triggers.map((t: any) => ({ ...t })) : nodeData.triggers,
            width: Math.max(nodeData.width ?? 220, 220),
            height: Math.max(nodeData.height ?? 98, 98),
          };
          layoutGraph.setNode(nodeId, nodeDataCopy);
        }
      });

      // Copy edges (preserve label so attribute->trigger edges get correct targetHandle)
      flowGraph.edges().forEach((edge) => {
        const edgeData = flowGraph.edge(edge);
        // Edge data can be a string (label) or an object
        if (typeof edgeData === "string") {
          layoutGraph.setEdge(edge.v, edge.w, { id: edgeData, label: edgeData });
        } else {
          layoutGraph.setEdge(edge.v, edge.w, edgeData || {});
        }
      });

      // Layout the graph
      dagre.layout(layoutGraph);

      // Convert to ReactFlow format (dagre.layout may mutate node data; guard against undefined)
      const nodes = layoutGraph.nodes().map((nodeId) => {
        const n = layoutGraph.node(nodeId);
        const nodeData = (n && typeof n === "object" ? n : {}) as any;
        const width = nodeData.width ?? 200;
        const height = nodeData.height ?? 100;
        const x = typeof nodeData.x === "number" ? nodeData.x : 0;
        const y = typeof nodeData.y === "number" ? nodeData.y : 0;
        return {
          id: nodeId,
          type: "node",
          data: {
            label: nodeData?.label ?? nodeId,
            node: nodeData,
            value: nodeData?.value ?? "",
            width,
            height,
            matchesSearch: false,
          },
          position: {
            x: x - width / 2,
            y: y - height / 2,
          },
          draggable: true,
        } as ReactFlowNode;
      });

      const edges = layoutGraph.edges().map((e) => {
        const edgeData = layoutGraph.edge(e);
        // Edge data can be a string (label) or an object after dagre layout; guard against undefined
        const edgeObj =
          typeof edgeData === "string"
            ? { label: edgeData, points: [] }
            : ((edgeData != null && typeof edgeData === "object" ? edgeData : {}) as any);
        const isBadEdge = Boolean(edgeObj.bad);
        let edgeLabel = (edgeObj.label ?? "") as string;
        // Ensure mapping node edges show labels: incoming "Uses", outgoing "Updates"
        if (!edgeLabel) {
          const sourceNode = layoutGraph.node(e.v) as { type?: string } | undefined;
          const targetNode = layoutGraph.node(e.w) as { type?: string } | undefined;
          if (targetNode?.type === "mapping" && edgeObj.targetHandle === "mapping") edgeLabel = "Uses";
          else if (sourceNode?.type === "mapping" && edgeObj.sourceHandle === "data") edgeLabel = "Updates";
          else if (
            sourceNode?.type === "trigger" &&
            targetNode?.type === "attribute" &&
            typeof edgeObj.sourceHandle === "string" &&
            edgeObj.sourceHandle.startsWith("task-")
          )
            edgeLabel = "Interview Goal";
        }
        return {
          id: `__${e.v}__${e.w}`,
          type: "smoothstep",
          source: e.v,
          target: e.w,
          sourceHandle: edgeObj.sourceHandle ?? undefined,
          targetHandle: edgeObj.targetHandle ?? undefined,
          animated: edgeObj.animated || false,
          label: edgeLabel,
          labelStyle: isBadEdge
            ? { fill: "var(--error-color, #d32f2f)", fontWeight: 600, fontSize: 11 }
            : edgeLabel
              ? { fontSize: 11 }
              : undefined,
          labelBgStyle: edgeLabel ? { fill: "var(--background, #fff)", fillOpacity: 0.9 } : undefined,
          points: edgeObj.points || [],
          style: {
            strokeWidth: isBadEdge ? 2 : edgeObj.width || 1,
            stroke: isBadEdge ? "var(--error-color, #d32f2f)" : undefined,
          },
          data: {
            height: 0,
            width: 0,
            label: edgeLabel,
            node: {},
            value: "",
            matchesSearch: false,
          },
        } as ReactFlowEdge;
      });

      const elements: Elements = [...nodes, ...edges] as Elements;
      setElementsState({ mode: "initialized", elements });
      onVisibleNodesChange?.(flowGraph.nodes());
      return;
    }
  }, [isFlowGraph, graph]);

  React.useEffect(() => {
    // don't use this path if flow graph
    if (isFlowGraph) {
      return;
    }
    // Skip if rawGraph hasn't been computed yet
    if (!rawGraph) {
      return;
    }

    // Check if dependencies actually changed to prevent infinite loops
    const rawGraphChanged = prevRawGraphRef.current !== rawGraphKey;
    const restrictedViewKey = recalculateKey?.toString();
    const restrictedViewChanged = prevRestrictedViewRootPathRef.current !== restrictedViewKey;
    if (restrictedViewChanged && selectedNode) {
      requiresSelectedNodeReview.current = true;
    }

    // If nothing actually changed, skip this effect
    if (!rawGraphChanged && !restrictedViewChanged && prevRawGraphRef.current !== null) {
      return;
    }

    // Update refs
    prevRawGraphRef.current = rawGraphKey;
    prevRestrictedViewRootPathRef.current = restrictedViewKey;

    // Regular graph processing via worker
    const msg: MsgToWorker = {
      id,
      rawGraph: rawGraph,
      showHidden,
      nodeHeight,
      rankDir,
      restrictedView: restrictedView
        ? {
            rootPath: restrictedView.rootPath, // safe to force because it's tested in isGraphViewRestricted
            graphDepth: restrictedView.graphDepth ?? 1,
            additionalVisibleNodes: [],
          }
        : undefined,
    };

    additionalVisibleNodes.current = [];
    if (!restrictedView?.rootPath) {
      const countVisibleNodes = showHidden
        ? (rawGraph.nodes || []).length
        : (rawGraph.nodes || []).filter((n) => n?.value && !n.value.hidden).length;
      if (countVisibleNodes > resolvedNodeRenderLimit && goalNodesOrdered.length > 0) {
        console.log(
          `Render limited to ${resolvedNodeRenderLimit} nodes. Preventing full display of ${countVisibleNodes} nodes.`,
        );

        let newAdditionalNodes: string[] = [];
        const maxExpansion = 25;
        for (const gn of goalNodesOrdered) {
          let newExpandedNodes: string[] = [];
          for (let currDepth = 1; currDepth <= maxExpansion; currDepth++) {
            const currPath = gn.path || gn.id;
            const allPredecessors = collectPredecessorsOrSuccessors(parsedGraph!, currPath, currDepth, "predecessors");
            const allSuccessors = collectPredecessorsOrSuccessors(parsedGraph!, currPath, currDepth, "successors");

            const newNodes = [currPath, ...allPredecessors, ...allSuccessors];
            if (
              newNodes.length === newExpandedNodes.length ||
              newNodes.length + newAdditionalNodes.length > resolvedNodeRenderLimit
            ) {
              break;
            }
            newExpandedNodes = newNodes;
          }
          newAdditionalNodes = [...new Set([...newAdditionalNodes, ...newExpandedNodes])];
        }

        msg.restrictedView = {
          rootPath: goalNodesOrdered[0].path || goalNodesOrdered[0].id,
          graphDepth: 1,
          additionalVisibleNodes: newAdditionalNodes,
        };
        additionalVisibleNodes.current = newAdditionalNodes;
      }
    }

    setElementsState((state) => {
      if (state.mode === "initializing" || state.mode === "refresh") {
        return state;
      }

      return { ...state, mode: state.mode === "initialized" ? "refresh" : "initializing" } as any;
    });

    console.log(`Showing graph ${restrictedView?.rootPath ? "with" : "without"} restrictions`);
    //console.log(msg);
    worker.postMessage(msg);
  }, [
    showHidden,
    rawGraphKey,
    nodeRenderLimit,
    recalculateKey,
    id,
    goalNodesOrdered.length,
    nodeHeight,
    rankDir,
    isFlowGraph,
  ]);

  const relayoutGraph = () => {
    if (elementsState.mode === "empty") {
      return;
    }

    console.log("Re-layout graph");

    const msgToWorker: MsgToWorker = {
      id,
      rawGraph: rawGraph,
      showHidden,
      nodeHeight,
      rankDir,
      restrictedView: restrictedView
        ? {
            rootPath: restrictedView.rootPath!, // safe to force because it's tested in isGraphViewRestricted
            graphDepth: restrictedView.graphDepth ?? 1,
            additionalVisibleNodes: [...(restrictedView.visibleNodes || [])],
          }
        : undefined,
    };

    // we might have some unfolds on an unrestricted graph that we need to preserve
    /*if (!restrictedView?.rootPath && additionalVisibleNodes.current.length && goalNodesOrdered?.[0]) {
      msgToWorker.restrictedView = {
        rootPath: goalNodesOrdered[0].path || goalNodesOrdered[0].id,
        graphDepth: 1,
        additionalVisibleNodes: additionalVisibleNodes.current,
      };
    }*/

    setElementsState(
      (state) =>
        ({
          ...state,
          mode: "refresh",
        }) as any,
    );
    requiresRefit.current = false;
    ignoreNewPositions.current = null;
    resetLayout.current = true;
    worker.postMessage(msgToWorker);
  };

  useUpdateEffect(() => {
    requiresRefit.current = true;
  }, [restrictedView?.rootPath]);

  React.useEffect(() => {
    // doing it this way goes back to the worker, and the whole graph gets re-layed out
    // we could append things to the existing elementsState.elements, but we'd have to work out ourselves where to put them
    // and it's nice to have the worker do the layout for us (even if we just tesselate the new nodes in the existing layout)

    if (restrictedView?.nodesToMerge?.length && restrictedView?.rootPath) {
      console.log(
        `Showing ${(restrictedView?.nodesToMerge || []).length} additional nodes reachable from ${restrictedView?.rootPath || "(Limitation not specified)"}`,
      );

      additionalVisibleNodes.current = [...(restrictedView?.visibleNodes || []), ...restrictedView?.nodesToMerge];
      const msgToWorker: MsgToWorker = {
        id,
        rawGraph: rawGraph,
        showHidden,
        nodeHeight,
        rankDir,
        restrictedView: restrictedView?.rootPath
          ? {
              rootPath: restrictedView?.rootPath,
              graphDepth: restrictedView?.graphDepth ?? 1,
              additionalVisibleNodes: additionalVisibleNodes.current,
            }
          : undefined,
      };
      restrictedView.nodesToMerge = null;
      // save old positions
      ignoreNewPositions.current = {
        repositionedNodes: ignoreNewPositions.current?.repositionedNodes || {},
        oldPositions: (elementsState.elements || []).reduce(
          (acc, el: any) => {
            if (el.position) {
              acc[el.id] = { x: el.position.x, y: el.position.y };
            }
            return acc;
          },
          {} as { [key: string]: { x: number; y: number } },
        ),
        // newNodes: restrictedViewNodesToMerge,
      };
      console.log("ignoreNewPositions.current", ignoreNewPositions.current);
      // seems counter intuitive, but we need to blank out the graph for a moment because whilst expanding
      // you can get weird artifacts like edges not drawing, despite the data being absolutely correct
      setElementsState(
        (state) =>
          ({
            ...state,
            mode: "refresh",
          }) as any,
      );
      worker.postMessage(msgToWorker);
    }
  }, [restrictedView?.nodesToMerge]);

  // -- rendering

  const renderRelayoutButton = () => {
    if (!relayoutGraph) {
      return null;
    }

    return (
      <RelayoutBtnContainer
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          relayoutGraph();
        }}
      >
        <Icon name="layout" />
      </RelayoutBtnContainer>
    );
  };

  const ignoreNextClick = React.useRef<boolean>(false);

  const handleNodeClick = React.useCallback<NonNullable<ReactFlowProps<any, any>["onNodeClick"]>>(
    (_, elementRaw) => {
      if (!isNode(elementRaw)) return;

      const targetId = (_?.target as any).id || "";
      const parentId = (_?.target as any)?.parentElement?.id || "";
      const parentParentId = (_?.target as any)?.parentElement?.parentElement?.id || "";
      const srcDomId = targetId || parentId || parentParentId;

      // catch any clicks that shouldn't have bubble up this far and drop them
      const isClickOnIgnoreEle = srcDomId.includes("graph_ignoreclick_");
      if (isClickOnIgnoreEle) {
        ignoreNextClick.current = true;
        return;
      }
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }

      const element = elementRaw as ReactFlowNode<any>;
      const isModKeyPressed = isMac() ? _.metaKey : _.ctrlKey;

      if (isModKeyPressed && onNodeCtrlClick) {
        onNodeCtrlClick(element.data.node.path || element.data.node.id);
      } else {
        // @ts-ignore
        onNodeClick?.(element.data.node.path || element.data.node.id, element.data.node);
      }
    },
    [onNodeClick, onNodeCtrlClick],
  );

  const [internalRefitKey, setInternalRefitKey] = React.useState<number>(0);

  const [fitted, setFitted] = React.useState<boolean>(false);

  useUpdateEffect(() => {
    if (selectedNode) {
      setInternalRefitKey((prev) => prev + 1);
    }
  }, [selectedNode]);

  if (elementsState.mode === "empty") {
    return null;
  }

  // Only show "No flows to display" if we've finished processing and there are no nodes
  if (nodes.length === 0 && elementsState.mode === "initialized" && !loading && fitted) {
    return <div className="relative h-full grow">No flows to display</div>;
  }
  return (
    <div className={`relative h-full grow [&_.${SelectedSearchNodeClsnm}]:outline-[2px] [&_.${SelectedSearchNodeClsnm}]:outline-[indianred]`}>
      <ReactFlowProvider>
        <GraphRefit
          onFit={() => setFitted(true)}
          refitKey={`${refitKey}_${internalRefitKey}`}
          elementsState={elementsState}
          centerElementId={selectedNode}
        />
        {elementsState.elements ? (
          <ReactFlow<any, any>
            style={{ width: "100%" }}
            minZoom={0.01}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            snapToGrid={false}
            onNodeDragStop={
              onRepositionNode
                ? (e, p) => {
                    if (p && p.position && p.id) {
                      repositionNode(p.id, p.position.x, p.position.y);
                    }
                  }
                : undefined
            }
            // nodesConnectable={false}
            nodesDraggable={true}
            draggable={true}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            {...flowGraphProps}
          >
            <StyledControls />
            {children}
            {renderRelayoutButton()}
          </ReactFlow>
        ) : null}
        {loading || !fitted || elementsState.mode === "refresh" || elementsState.mode === "initializing" ? (
          <div
            className="absolute inset-0 z-[100] flex items-center justify-center"
            style={{ background: `rgba(255,255,255,${!fitted ? 1 : 0.5})` }}
          >
            <LoadingSpinner size="sm"  />
          </div>
        ) : null}
      </ReactFlowProvider>
    </div>
  );
};

export default Object.assign(GraphView, {
  Background: Background,
  Handle: Handle,
  Position: Position,
  useStore: useStore,
});
